'use client';

/**
 * The footer under an SDL text field: the ONE resolved message (error, success or helper, from the shared
 * `deriveInputMessage`) and, when asked, the character counter. Spelled once for `Input` and `Textarea` -- it began as
 * `Textarea`'s private footer, and giving `Input` a counter by copying it would have been a second spelling of the same
 * footer (2026-08-21, the groups name/description caps). Renders nothing when there is neither a message nor a count.
 */

import { FLEX_BETWEEN_STYLE } from '@federa/const/style-flex-presets';
import { getCharCountStyles } from './form-utils.ts';
import type * as React from 'react';
import type { FormFieldState } from '@federa/types/ui-form-field';
import type { InputContentBaseProps } from '@federa/types/ui-input';

export function FieldFooter({
	messageId,
	message,
	state,
	showCount,
	charCount,
	maxLength,
	messageStyles,
}: {
	/** The id the field's `aria-describedby` names; the message span carries it whether or not a message is present. */
	messageId: string;
	message: string | undefined;
	state: FormFieldState;
	showCount: boolean;
	charCount: number;
	maxLength: number | undefined;
	/** The resolved helper-text styles for the field's state; the footer lays the message and the count out over them. */
	messageStyles: React.CSSProperties;
}): React.ReactElement | null {
	if (!message && !showCount) return null;

	return (
		<div style={{ ...messageStyles, ...FLEX_BETWEEN_STYLE }}>
			<span id={messageId} role={state === 'error' ? 'alert' : undefined}>
				{message}
			</span>
			{showCount && (
				<span style={getCharCountStyles({ count: charCount, maxLength })}>
					{charCount}
					{maxLength ? `/${maxLength}` : ''}
				</span>
			)}
		</div>
	);
}

/**
 * The members {@link FieldFooter} reads off the shared `Input` variant contract, named as a `Pick` of it rather than
 * re-listed. The two RENAMES are the whole reason the forward could not be a spread: the contract calls them
 * `helperId` and `displayHelperText` where the footer, which `Textarea` also renders, calls them `messageId` and
 * `message` -- a generic footer vocabulary that deliberately does not name the input's helper text.
 */
type FieldFooterContentProps = Pick<InputContentBaseProps, 'helperId' | 'displayHelperText' | 'state' | 'showCount' | 'charCount' | 'maxLength'>;

/**
 * {@link FieldFooter} bound to the shared `Input` variant contract: six of its seven arguments ride the bag and only
 * the style recipe differs per variant (the default variant's resolved `formFieldStyles.helperTextStyles`, the
 * floating variant's `buildFormFieldHelperTextStyles(state)`).
 *
 * BOTH VARIANTS SPELLED THOSE SIX FORWARDS BY HAND, which is the same half-SSOT `ContentInputCore`
 * (`components/primitives/input-components.tsx`) cures one element over: the SHAPE was declared once on
 * `InputContentBaseProps` and the FORWARD twice, so a member added to the contract reached the footer in whichever
 * variant the author remembered. `Textarea` deliberately does NOT come here -- it composes its message id and style
 * from its own helpers rather than off this bag, so binding it would be a coupling, not an adoption.
 */
export function ContentFieldFooter(args: {
	content: FieldFooterContentProps;
	messageStyles: React.CSSProperties;
}): React.ReactElement | null {
	return (
		<FieldFooter
			messageId={args.content.helperId}
			message={args.content.displayHelperText}
			state={args.content.state}
			showCount={args.content.showCount}
			charCount={args.content.charCount}
			maxLength={args.content.maxLength}
			messageStyles={args.messageStyles}
		/>
	);
}
